import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SLIDES = [
  {
    key: 'hot',
    badge: 'Hot picks',
    title: 'Discover new manufacturers',
    subtitle: 'Verified catalogs, competitive MOQs, and buyer protections on every order.',
    cta: 'View more',
    href: '/top-seller',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80',
    gradient: 'from-sky-900/92 via-blue-900/88 to-indigo-950/95',
  },
  {
    key: 'source',
    badge: 'Conflux sourcing',
    title: 'Source smarter with verified suppliers',
    subtitle: 'RFQs, factory-direct pricing, and global shipping — built for B2B teams.',
    cta: 'Browse products',
    href: '/products',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80',
    gradient: 'from-slate-900/94 via-slate-800/90 to-emerald-950/92',
  },
  {
    key: 'deals',
    badge: 'Top deals',
    title: 'Score the lowest prices on the marketplace',
    subtitle: 'Curated listings with strong seller ratings and transparent MOQs.',
    cta: 'See deals',
    href: '/featured-products',
    image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200&q=80',
    gradient: 'from-orange-950/90 via-amber-900/85 to-stone-900/95',
  },
];

/**
 * Right-column promo carousel (Alibaba-style auto-sliding banner).
 */
export default function MarketplaceHomePromoPanel({ onSendRequirement, onBecomeSupplier, onFindBuyers }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <aside
      className="mh-promo-tall mh-bento-promo-col"
      style={{
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
        border: '1px solid #e8e8e8',
        borderTop: '3px solid #ff6a00',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
        minHeight: 0,
        height: '100%',
      }}
      aria-roledescription="carousel"
      aria-label="Featured promotions"
    >
      {/* Slides */}
      <div
        className="mh-promo-carousel"
        style={{ position: 'relative', flex: '1 1 auto', minHeight: 280 }}
      >
        {SLIDES.map((s, i) => (
          <div
            key={s.key}
            className={`mh-promo-slide ${i === idx ? 'mh-promo-slide--active' : ''}`}
            aria-hidden={i !== idx}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${s.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transform: 'scale(1.05)',
              }}
            />
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.gradient}`}
              style={{ position: 'absolute', inset: 0 }}
            />
            <div
              style={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                height: '100%',
                minHeight: 280,
                padding: '20px 20px 22px',
              }}
            >
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'rgba(255,255,255,0.75)',
                  marginBottom: 6,
                }}
              >
                {s.badge}
              </p>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  marginBottom: 8,
                  textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }}
              >
                {s.title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.85)',
                  lineHeight: 1.5,
                  marginBottom: 18,
                  maxWidth: 340,
                }}
              >
                {s.subtitle}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => navigate(s.href)}
                  style={{
                    borderRadius: 999,
                    background: '#fff',
                    color: '#1b1b1b',
                    fontWeight: 600,
                    padding: '9px 20px',
                    fontSize: 13,
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transition: 'opacity .2s ease',
                  }}
                >
                  {s.cta}
                </button>
                <button
                  type="button"
                  onClick={onSendRequirement}
                  style={{
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.4)',
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(4px)',
                    color: '#fff',
                    fontWeight: 600,
                    padding: '9px 20px',
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'background .2s ease',
                  }}
                >
                  Send requirement
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dots + links bar */}
      <div
        style={{
          position: 'relative',
          zIndex: 5,
          background: 'rgba(0,0,0,0.35)',
          backdropFilter: 'blur(6px)',
          padding: '10px 16px',
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <div className="mh-promo-dots">
            {SLIDES.map((s, i) => (
              <button
                key={s.key}
                type="button"
                aria-label={`Slide ${i + 1}: ${s.title}`}
                aria-current={i === idx ? 'true' : undefined}
                className={`mh-promo-dot ${i === idx ? 'mh-promo-dot--active' : ''}`}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
          <span
            style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', color: 'rgba(255,255,255,0.9)', fontVariantNumeric: 'tabular-nums' }}
            aria-live="polite"
          >
            {idx + 1} / {SLIDES.length}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 16px', marginTop: 8 }}>
          <button
            type="button"
            onClick={onBecomeSupplier}
            style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            Become a supplier
          </button>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>|</span>
          <button
            type="button"
            onClick={onFindBuyers}
            style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            Find buyers
          </button>
        </div>
      </div>
    </aside>
  );
}
